import { z } from 'zod';

/**
 * The workspace as the SPA sees it - ADR-030 and API-004.
 *
 * The role and status vocabularies live here rather than in `@coworker/db`
 * because both the browser and the database need them and only one of the two
 * can import the other. `packages/db` imports these; it does not redefine them.
 * Two definitions of an authorization vocabulary drift the first time only one
 * is edited, and the drift is silent - a role added to the check constraint but
 * not here decodes fine and is then refused by a schema nobody looked at.
 */

/** The three roles `0001_tenancy` allows. DATA-008 replaces this with a table; it is P3. */
export const MEMBERSHIP_ROLES = ['OWNER', 'ADMIN', 'MEMBER'] as const;
export type MembershipRole = (typeof MEMBERSHIP_ROLES)[number];

export const MEMBERSHIP_STATUSES = ['INVITED', 'ACTIVE', 'SUSPENDED'] as const;
export type MembershipStatus = (typeof MEMBERSHIP_STATUSES)[number];

/**
 * Only an `ACTIVE` membership authorizes anything. An invitation that has not
 * been accepted and a suspended member both read as "not a member" at the API.
 */
export const AUTHORIZING_STATUS = 'ACTIVE' satisfies MembershipStatus;

/**
 * The same expression as the `workspace_slug_check` constraint in
 * `0001_tenancy.up.sql`, so the API refuses a bad slug with a field-level
 * message instead of letting Postgres refuse it with a constraint name.
 */
export const WORKSPACE_SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{1,62}$/;

/**
 * The database puts no ceiling on a name. The API does: an unbounded string
 * from an unauthenticated-shaped field is an input-validation gap even when the
 * column would hold it. Deliberately stricter than the schema, not looser.
 */
export const WORKSPACE_NAME_MAX = 120;

export const workspaceSchema = z
  .object({
    id: z.uuid(),
    name: z.string(),
    slug: z.string(),
    region: z.string(),
    createdAt: z.string(),
  })
  .readonly();

/** A workspace plus how the caller relates to it. What `/v1/workspaces` lists. */
export const workspaceSummarySchema = z
  .object({
    id: z.uuid(),
    name: z.string(),
    slug: z.string(),
    role: z.enum(MEMBERSHIP_ROLES),
    status: z.enum(MEMBERSHIP_STATUSES),
  })
  .readonly();

export const createWorkspaceRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(WORKSPACE_NAME_MAX),
    slug: z
      .string()
      .regex(
        WORKSPACE_SLUG_PATTERN,
        'a slug is 2-63 characters of lowercase letters, digits and hyphens, and starts with a letter or digit',
      ),
  })
  .readonly();

export const workspaceListResponseSchema = z
  .object({ workspaces: z.array(workspaceSummarySchema).readonly() })
  .readonly();

export const workspaceResponseSchema = z
  .object({ workspace: workspaceSchema, role: z.enum(MEMBERSHIP_ROLES) })
  .readonly();

export type Workspace = z.infer<typeof workspaceSchema>;
export type WorkspaceSummary = z.infer<typeof workspaceSummarySchema>;
export type CreateWorkspaceRequest = z.infer<typeof createWorkspaceRequestSchema>;
export type WorkspaceListResponse = z.infer<typeof workspaceListResponseSchema>;
export type WorkspaceResponse = z.infer<typeof workspaceResponseSchema>;
